/**
 * APTabs Component
 *
 * A tabs container component following Figma design system specifications.
 * Uses native button elements with apds-tab* classes.
 *
 * @example
 * ```tsx
 * const [activeTab, setActiveTab] = useState('tab1');
 *
 * const tabs = [
 *   { id: 'tab1', label: 'Tab 1' },
 *   { id: 'tab2', label: 'Tab 2' },
 *   { id: 'tab3', label: 'Tab 3' }
 * ];
 *
 * <APTabs
 *   tabs={tabs}
 *   activeTab={activeTab}
 *   onTabChange={setActiveTab}
 *   variant="button"
 * />
 * ```
 */

import { useCallback, useId, useRef } from 'react';

import clsx from 'clsx';

import { APScrollContainer } from '@shared/components/molecules/scroll-container';

import type { IAPTabsProps, ITabChangeEvent } from './types';

import './styles.scss';

/**
 * APTabs - Production-ready tabs container component
 *
 * Follows AstroPay design system and accessibility guidelines.
 */
export const APTabs = ({
  tabs,
  activeTab,
  onTabChange,
  variant = 'button',
  disabled = false,
  orientation = 'horizontal',
  size: _size = 'medium',
  className = '',
  scrollable = true,
  scrollStep,
  'data-testid': testId,
}: IAPTabsProps) => {
  const tabsId = useId();
  const tabsRef = useRef<HTMLDivElement>(null);

  // Handle tab selection
  const handleTabClick = useCallback(
    (tabId: string) => {
      if (disabled) return;

      const tabIndex = tabs.findIndex((tab) => tab.id === tabId);
      const tab = tabs[tabIndex];

      if (!tab || tab.disabled) return;

      onTabChange(tabId);
    },
    [tabs, onTabChange, disabled]
  );

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      const { key } = event;
      const currentIndex = tabs.findIndex((tab) => tab.id === activeTab);
      let nextIndex = currentIndex;

      switch (key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          event.preventDefault();
          nextIndex = currentIndex > 0 ? currentIndex - 1 : tabs.length - 1;
          break;
        case 'ArrowRight':
        case 'ArrowDown':
          event.preventDefault();
          nextIndex = currentIndex < tabs.length - 1 ? currentIndex + 1 : 0;
          break;
        case 'Home':
          event.preventDefault();
          nextIndex = 0;
          break;
        case 'End':
          event.preventDefault();
          nextIndex = tabs.length - 1;
          break;
        default:
          return;
      }

      // Find next enabled tab
      const enabledTabs = tabs.filter((tab) => !tab.disabled);
      if (enabledTabs.length === 0) return;

      // Find the next non-disabled tab
      let attempts = 0;
      while (attempts < tabs.length) {
        const nextTab = tabs[nextIndex];
        if (nextTab && !nextTab.disabled) {
          handleTabClick(nextTab.id);
          break;
        }

        // Move to next index and wrap around
        if (key === 'ArrowRight' || key === 'ArrowDown') {
          nextIndex = nextIndex < tabs.length - 1 ? nextIndex + 1 : 0;
        } else if (key === 'ArrowLeft' || key === 'ArrowUp') {
          nextIndex = nextIndex > 0 ? nextIndex - 1 : tabs.length - 1;
        }

        attempts++;
      }
    },
    [tabs, activeTab, handleTabClick]
  );

  const renderTabButtons = () =>
    tabs.map((tab, index) => {
      const isSelected = tab.id === activeTab;
      const isDisabled = disabled || tab.disabled;

      const tabClassName = clsx(
        'apds-tab',
        variant === 'line' && 'apds-tab--line',
        isSelected && 'apds-tab--selected',
        isDisabled && 'apds-tab--disabled'
      );

      return (
        <button
          key={tab.id}
          type="button"
          disabled={isDisabled}
          onClick={() => handleTabClick(tab.id)}
          className={tabClassName}
          data-testid={testId ? `${testId}-tab-${index}` : `tab-${index}`}
          aria-controls={`${tabsId}-panel-${tab.id}`}
          aria-selected={isSelected}
          aria-disabled={isDisabled}
          role="tab"
          id={`${tabsId}-tab-${tab.id}`}
          {...tab.tabProps}
        >
          {tab.label}
        </button>
      );
    });

  const tabButtons = renderTabButtons();

  const tabsClasses = clsx(
    'apds-tabs',
    orientation === 'vertical' && 'apds-tabs--vertical',
    className
  );

  return (
    <div
      ref={tabsRef}
      className={tabsClasses}
      data-testid={testId}
      role="tablist"
      aria-orientation={orientation}
      onKeyDown={handleKeyDown}
    >
      {scrollable && orientation === 'horizontal' ? (
        <APScrollContainer
          className="apds-tabs__scroll"
          step={scrollStep}
          data-testid={testId ? `${testId}-scroll` : undefined}
        >
          {tabButtons}
        </APScrollContainer>
      ) : (
        tabButtons
      )}
    </div>
  );
};

APTabs.displayName = 'APTabs';

export type { IAPTabsProps, ITabChangeEvent };
export default APTabs;
